import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { EventItem } from '@/types/api'
import { formatDateTime } from '@/utils/format'
import { usePermission } from '@/hooks/use-permission'
import type { CombinedColumnDef } from '@/api/dowload-excel.api'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  canView?: boolean
  canUpdate?: boolean
  onViewDetail?: (event: EventItem) => void
  onUpdate?: (event: EventItem) => void
  onDelete?: (event: EventItem) => void
  onToggleStatus?: (event: EventItem) => void
}

interface EventActionsCellProps {
  event: EventItem
  onViewDetail?: (event: EventItem) => void
  onUpdate?: (event: EventItem) => void
  onDelete?: (event: EventItem) => void
}

export function buildColumns(
  options: BuildColumnsOptions,
): CombinedColumnDef<EventItem>[] {
  const {
    pageIndex,
    pageSize,
    canView = true,
    canUpdate = true,
    onViewDetail,
    onUpdate,
    onDelete,
    onToggleStatus,
  } = options

  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
      align: 'center',
    },
    {
      accessorKey: 'name',
      header: 'TÊN SỰ KIỆN',
      size: 150,
      cell: ({ row }) =>
        canView ? (
          <button
            type="button"
            className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a]"
            onClick={() => onViewDetail?.(row.original)}
          >
            {row.original.name}
          </button>
        ) : (
          <span className="text-[#CE7A58]">{row.original.name}</span>
        ),
      align: 'left',
    },
    {
      accessorKey: 'startDate',
      header: 'BẮT ĐẦU',
      size: 100,
      cell: ({ getValue }) => formatDateTime(getValue(), 'DD/MM/YYYY'),
      align: 'center',
    },
    {
      accessorKey: 'endDate',
      header: 'KẾT THÚC',
      size: 100,
      cell: ({ getValue }) => formatDateTime(getValue(), 'DD/MM/YYYY'),
      align: 'center',
    },
    {
      accessorKey: 'updatedAt',
      header: 'NGÀY CẬP NHẬT',
      size: 100,
      cell: ({ getValue }) => formatDateTime(getValue()),
      align: 'center',
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI',
      size: 80,
      cell: ({ row }) => {
        const event = row.original
        return (
          <Switch
            checked={event.status === 1}
            onCheckedChange={() => canUpdate && onToggleStatus?.(event)}
            disabled={!canUpdate}
          />
        )
      },
      align: 'center',
    },
    {
      id: 'actions',
      size: 60,
      exportable: false,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <EventActionsCell
            event={row.original}
            onViewDetail={onViewDetail}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        </div>
      ),
    },
  ]
}

function EventActionsCell({
  event,
  onViewDetail,
  onUpdate,
  onDelete,
}: EventActionsCellProps) {
  const canView = usePermission('event-view')
  const canUpdate = usePermission('event-update')
  const canDelete = usePermission('event-delete')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {canView && (
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => onViewDetail?.(event)}
          >
            Xem chi tiết
          </DropdownMenuItem>
        )}
        {canUpdate && (
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => onUpdate?.(event)}
          >
            Cập nhật
          </DropdownMenuItem>
        )}
        {canDelete && (
          <DropdownMenuItem
            className="cursor-pointer text-red-600"
            onClick={() => onDelete?.(event)}
          >
            Xóa
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
